import { useRef, useState, useCallback, useEffect } from 'react';
import { formatUnitIdShort, nextAutoUniqueIdForPrefix, ssCodeBare } from '../utils/unitIdFormat';
import { resolveUnitTypeDisplayName } from '../utils/unitDisplay';
import AuthImage from './AuthImage';
import { Plus, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import {
  INSPECTION_RESULT,
  normalizeInspectionResult,
  inspectionResultChipClass,
} from '../utils/inspectionResultColor';

function toPlanPercent(x, y) {
  if (x == null || y == null || x === '' || y === '') return null;
  const nx = Number(x);
  const ny = Number(y);
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  if (nx >= 0 && nx <= 1 && ny >= 0 && ny <= 1) return { x: nx * 100, y: ny * 100 };
  return { x: nx, y: ny };
}

function isPlaced(unit) {
  return toPlanPercent(unit.floor_plan_x, unit.floor_plan_y) != null;
}

function withDisplayCoords(units) {
  return (units || []).map((u, i) => {
    const placed = toPlanPercent(u.floor_plan_x, u.floor_plan_y);
    if (placed) return { ...u, floor_plan_x: placed.x, floor_plan_y: placed.y, _autoPin: false };
    return {
      ...u,
      floor_plan_x: 14 + (i % 5) * 18,
      floor_plan_y: 18 + Math.floor(i / 5) * 16,
      _autoPin: true,
    };
  });
}

function percentFromPointer(rect, clientX, clientY) {
  return {
    x: Math.max(0, Math.min(100, Number((((clientX - rect.left) / rect.width) * 100).toFixed(2)))),
    y: Math.max(0, Math.min(100, Number((((clientY - rect.top) / rect.height) * 100).toFixed(2)))),
  };
}

const LABEL_PX_MIN = 7;
const LABEL_PX_MAX = 18;
const LABEL_PX_DEFAULT = 10;
const LABEL_PX_STORAGE = 'bwof_floor_label_px_v2';

function readStoredLabelPx() {
  try {
    const n = Number(localStorage.getItem(LABEL_PX_STORAGE));
    if (Number.isFinite(n) && n >= LABEL_PX_MIN && n <= LABEL_PX_MAX) return n;
  } catch {
    /* ignore */
  }
  return LABEL_PX_DEFAULT;
}

function unitInspectRaw(unit) {
  if (!unit) return '';
  return (
    unit.inspection_result ||
    unit.inspect_status ||
    unit.last_outcome ||
    unit.outcome ||
    unit._inspectStatus ||
    ''
  );
}

function UnitMarker({
  unit,
  highlighted,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
  scale = 1,
  labelPx = LABEL_PX_DEFAULT,
}) {
  const x = Number(unit.floor_plan_x);
  const y = Number(unit.floor_plan_y);
  const fullId = formatUnitIdShort(unit.unique_id, unit.ss_code);
  const bare = ssCodeBare(unit.ss_code);
  let label = fullId || bare;
  if (bare && label) {
    const prefix = `${bare}-`;
    if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
      label = label.slice(prefix.length) || label;
    }
  }
  const typeName = String(resolveUnitTypeDisplayName(unit) || '').trim();
  const isDraft = String(unit.id || '').startsWith('draft-');
  const autoPin = !!unit._autoPin;
  const result = normalizeInspectionResult(unitInspectRaw(unit));
  // Counter-scale so markers keep a constant on-screen size while the plan zooms
  const inv = scale > 0.01 ? 1 / scale : 1;
  let colorClass = 'border-primary-600 text-primary-800 bg-white';
  if (highlighted) {
    colorClass = 'border-amber-500 text-amber-900 bg-amber-50 ring-2 ring-amber-300';
  } else if (
    result === INSPECTION_RESULT.PASS ||
    result === INSPECTION_RESULT.FAIL ||
    result === INSPECTION_RESULT.INCOMPLETE
  ) {
    colorClass = inspectionResultChipClass(result);
  } else if (isDraft || autoPin) {
    colorClass = 'border-amber-500 text-amber-900 bg-white' + (autoPin ? ' border-dashed' : '');
  }

  return (
    <div
      data-marker
      role="button"
      tabIndex={0}
      className={`absolute z-10 cursor-grab active:cursor-grabbing select-none px-1 py-0.5 font-bold leading-none border-2 shadow-md text-center whitespace-nowrap ${colorClass}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) scale(${inv})`,
        fontSize: `${labelPx}px`,
        transformOrigin: 'center center',
      }}
      onPointerDown={(e) => onPointerDown(e, unit)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick?.(unit);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu?.(unit);
      }}
      title={`${fullId}${typeName ? ` · ${typeName}` : ''}${
        result !== INSPECTION_RESULT.NOT_TESTED ? ` · ${result}` : ''
      } — drag to move · click for details · right-click to delete${isDraft ? ' (new)' : ''}`}
    >
      <span className="block">{label}{isDraft ? '*' : ''}</span>
    </div>
  );
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;

function EquipmentTypeChips({
  instance,
  numberingUnits,
  selectedTypeId,
  onSelectType,
  showAllSubs,
  onShowAllSubsChange,
}) {
  const types = instance?.equipment_types || [];
  const bare = ssCodeBare(instance?.ss_code) || instance?.ss_code || '';
  if (!instance) return null;
  if (!types.length) {
    return (
      <p className="text-xs text-amber-800 mt-2">
        No subs on this SS — clicks place {bare || '9'}-1, {bare || '9'}-2, …
      </p>
    );
  }
  return (
    <div className="mt-2 space-y-2" data-no-place>
      <p className="text-[11px] font-medium text-gray-600">Subs (pick one, then click the drawing)</p>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {types.map((t) => {
          const nextId = nextAutoUniqueIdForPrefix(t.prefix, numberingUnits);
          const selected = String(selectedTypeId) === String(t.id);
          return (
            <button
              key={t.id}
              type="button"
              data-no-place
              onClick={() => onSelectType?.(t.id)}
              className={`px-1 py-0.5 text-xs font-semibold bg-transparent border-0 ${
                selected ? 'text-blue-600 underline' : 'text-slate-700 hover:text-blue-500'
              }`}
              title={`Place ${nextId} — click the drawing after selecting`}
            >
              <span className="font-mono">{t.prefix}</span>
              <span className="ml-1 font-medium">{t.label}</span>
              <span className={`ml-1 font-normal ${selected ? 'text-blue-500' : 'text-slate-400'}`}>
                → {String(nextId).split('-').pop()}
              </span>
            </button>
          );
        })}
      </div>
      <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-700">
        <legend className="sr-only">Drawing view</legend>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`ss-sub-view-${instance.id}`}
            checked={!showAllSubs}
            onChange={() => onShowAllSubsChange?.(false)}
          />
          This sub only
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`ss-sub-view-${instance.id}`}
            checked={!!showAllSubs}
            onChange={() => onShowAllSubsChange?.(true)}
          />
          All SS {bare} subs on one plan
        </label>
      </fieldset>
      {!showAllSubs && types.length ? (
        <p className="text-[11px] text-slate-500">
          Registered: {types.map((t) => t.label || t.prefix).filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {showAllSubs ? (
        <p className="text-xs text-primary-800">
          Showing every {bare}-sub on this drawing. Pick a sub to place the next unit of that type.
        </p>
      ) : selectedTypeId ? (
        <p className="text-xs text-primary-800">Click the drawing to place the next unit of this sub.</p>
      ) : (
        <p className="text-xs text-amber-700">Select a sub, then click the drawing.</p>
      )}
    </div>
  );
}

/**
 * Floor plan: select SS (number only), pick a registered sub-type, click drawing to place.
 * Click marker ID to inspect; click type name for the question popup; right-click to delete.
 */
/** Fit natural image size into max box (CSS px). */
function fitPlanBox(nw, nh, maxW, maxH) {
  if (!nw || !nh || !maxW || !maxH) return { w: 0, h: 0 };
  const s = Math.min(maxW / nw, maxH / nh);
  return {
    w: Math.max(1, Math.round(nw * s)),
    h: Math.max(1, Math.round(nh * s)),
  };
}

export default function FloorPlanCanvas({
  floorPlanUrl,
  units,
  numberingUnits,
  ssInstances = [],
  highlightUnitId,
  selectedInstanceId,
  onSelectInstance,
  selectedEquipmentTypeId,
  onSelectEquipmentType,
  showAllSubs = false,
  onShowAllSubsChange,
  onPlaceUnit,
  onMoveUnit,
  onDeleteUnit,
  onUnitClick,
  onUnitDoubleClick,
  onUnitNameClick,
  onHighlightUnit,
  onManagePlans,
}) {
  const mapRef = useRef(null);
  const imageWrapRef = useRef(null);
  const viewportRef = useRef(null);
  const stageHostRef = useRef(null);
  const naturalRef = useRef({ w: 0, h: 0 });
  const viewRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  /** Display box matching drawing aspect (no empty slate around the plan). */
  const [planBox, setPlanBox] = useState({ w: 0, h: 0 });
  const [labelPx, setLabelPx] = useState(readStoredLabelPx);
  const [dragging, setDragging] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const panRef = useRef(null);
  const planClickRef = useRef(null);
  const markerClickRef = useRef(null);
  /** Ignore place briefly after picking SS/sub (avoids accidental create). */
  const suppressPlaceUntilRef = useRef(0);

  const armPlaceSuppress = useCallback((ms = 400) => {
    suppressPlaceUntilRef.current = Date.now() + ms;
    planClickRef.current = null;
  }, []);

  const recomputePlanBox = useCallback(() => {
    const { w: nw, h: nh } = naturalRef.current;
    if (!nw || !nh) return;
    const host = stageHostRef.current;
    const maxW = Math.max(160, host?.clientWidth || 640);
    // Fit drawing to canvas as large as practical (prefer filling the stage host)
    const maxH = Math.max(200, Math.min(window.innerHeight * 0.72, 900));
    setPlanBox(fitPlanBox(nw, nh, maxW, maxH));
  }, []);

  const bumpLabelPx = (delta, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // Keep scroll / plan pan ?? avoid button focus scrolling the page to the top
    if (e?.currentTarget && typeof e.currentTarget.blur === 'function') {
      try {
        e.currentTarget.blur();
      } catch {
        /* ignore */
      }
    }
    setLabelPx((prev) => {
      const next = Math.max(LABEL_PX_MIN, Math.min(LABEL_PX_MAX, prev + delta));
      try {
        localStorage.setItem(LABEL_PX_STORAGE, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  // Reset zoom / size when plan image changes
  useEffect(() => {
    setView({ scale: 1, tx: 0, ty: 0 });
    naturalRef.current = { w: 0, h: 0 };
    setPlanBox({ w: 0, h: 0 });
  }, [floorPlanUrl]);

  useEffect(() => {
    const host = stageHostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recomputePlanBox());
    ro.observe(host);
    window.addEventListener('resize', recomputePlanBox);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recomputePlanBox);
    };
  }, [recomputePlanBox, floorPlanUrl]);

  const onPlanImageLoad = useCallback(
    (e) => {
      const img = e?.currentTarget;
      const nw = img?.naturalWidth || 0;
      const nh = img?.naturalHeight || 0;
      if (!nw || !nh) return;
      naturalRef.current = { w: nw, h: nh };
      recomputePlanBox();
    },
    [recomputePlanBox]
  );

  const clampPan = useCallback((next) => {
    const vp = viewportRef.current;
    if (!vp) return next;
    const w = planBox.w || vp.clientWidth;
    const h = planBox.h || vp.clientHeight;
    if (!w || !h) return next;
    let { scale, tx, ty } = next;
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
    if (scale <= 1.01) {
      return { scale: 1, tx: 0, ty: 0 };
    }
    const maxX = w * (scale - 1);
    const maxY = h * (scale - 1);
    tx = Math.min(0, Math.max(-maxX, tx));
    ty = Math.min(0, Math.max(-maxY, ty));
    return { scale, tx, ty };
  }, [planBox.w, planBox.h]);

  const setScaleAt = useCallback(
    (newScale, clientX, clientY) => {
      const vp = viewportRef.current;
      const prev = viewRef.current;
      let scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      if (!vp) {
        setView(clampPan({ ...prev, scale }));
        return;
      }
      // +/- buttons: zoom toward viewport centre so the toolbar stays put visually
      let cx = clientX;
      let cy = clientY;
      if (cx == null || cy == null) {
        const rect = vp.getBoundingClientRect();
        cx = rect.left + rect.width / 2;
        cy = rect.top + rect.height / 2;
      }
      const rect = vp.getBoundingClientRect();
      const px = cx - rect.left;
      const py = cy - rect.top;
      const ratio = scale / prev.scale;
      const tx = px - ratio * (px - prev.tx);
      const ty = py - ratio * (py - prev.ty);
      setView(clampPan({ scale, tx, ty }));
    },
    [clampPan]
  );

  const [planImageFailed, setPlanImageFailed] = useState(false);

  useEffect(() => {
    setPlanImageFailed(false);
  }, [floorPlanUrl]);

  useEffect(() => {
    if (!planBox.w || !planBox.h) return;
    setView((prev) => clampPan(prev));
  }, [planBox.w, planBox.h, clampPan]);

  const placed = units.filter(isPlaced);
  const autoCount = units.length - placed.length;

  const displayUnits = withDisplayCoords(units).map((u) => {
    if (dragging === u.id && dragPos) {
      return { ...u, floor_plan_x: dragPos.x, floor_plan_y: dragPos.y, _autoPin: false };
    }
    return u;
  });
  const placedDisplay = displayUnits;

  const handleMarkerPointerDown = (e, unit) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    e.preventDefault();
    onHighlightUnit?.(unit.id);
    markerClickRef.current = { id: unit.id, x: e.clientX, y: e.clientY, moved: false };
    setDragging(unit.id);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging || !mapRef.current) return;
      const start = markerClickRef.current;
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6) {
        start.moved = true;
      }
      const wrap = imageWrapRef.current || mapRef.current;
      const rect = wrap.getBoundingClientRect();
      setDragPos(percentFromPointer(rect, e.clientX, e.clientY));
    },
    [dragging]
  );

  const handlePointerUp = useCallback(() => {
    const click = markerClickRef.current;
    markerClickRef.current = null;
    if (click && !click.moved) {
      const unit = units.find((u) => String(u.id) === String(click.id));
      setDragging(null);
      setDragPos(null);
      if (unit) onUnitClick?.(unit);
      return;
    }
    if (!dragging || !dragPos) {
      setDragging(null);
      setDragPos(null);
      return;
    }
    onMoveUnit?.(dragging, dragPos.x, dragPos.y);
    setDragging(null);
    setDragPos(null);
  }, [dragging, dragPos, onMoveUnit, onUnitClick, units]);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragging, handlePointerMove, handlePointerUp]);

  // Wheel on the plan scrolls the page (never zooms). Zoom is +/- only.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (e) => {
      let el = vp.parentElement;
      while (el) {
        const st = window.getComputedStyle(el);
        const oy = st.overflowY;
        if (
          (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
          el.scrollHeight > el.clientHeight + 1
        ) {
          el.scrollTop += e.deltaY;
          e.preventDefault();
          return;
        }
        el = el.parentElement;
      }
      window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
      e.preventDefault();
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [floorPlanUrl, planBox.w, planBox.h]);

  // Clear stale armed place-clicks (e.g. pointerdown on plan, release on Sub chip)
  useEffect(() => {
    const clearArmed = (e) => {
      const vp = viewportRef.current;
      // Let the viewport handler own place when release is on the plan stage
      if (vp && (e.target === vp || vp.contains(e.target))) return;
      planClickRef.current = null;
    };
    window.addEventListener('pointerup', clearArmed, true);
    window.addEventListener('pointercancel', clearArmed, true);
    return () => {
      window.removeEventListener('pointerup', clearArmed, true);
      window.removeEventListener('pointercancel', clearArmed, true);
    };
  }, []);

  // Changing SS / sub must never place a unit by itself
  useEffect(() => {
    armPlaceSuppress(500);
  }, [selectedInstanceId, selectedEquipmentTypeId, armPlaceSuppress]);

  const onViewportPointerDown = (e) => {
    if (dragging) return;
    if (e.target?.closest?.('[data-marker]')) return;
    if (e.target?.closest?.('[data-zoom-btn]')) return;
    if (e.target?.closest?.('[data-no-place]')) return;
    if (e.button !== 0) return;

    const wrap = imageWrapRef.current;
    if (!wrap) {
      planClickRef.current = null;
      return;
    }
    const rect = wrap.getBoundingClientRect();
    const onDrawing =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;
    if (!onDrawing) {
      planClickRef.current = null;
      return;
    }

    planClickRef.current = {
      x: e.clientX,
      y: e.clientY,
      pointerId: e.pointerId,
    };
    if (viewRef.current.scale <= 1.02) return;
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      tx: viewRef.current.tx,
      ty: viewRef.current.ty,
      pointerId: e.pointerId,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onViewportPointerMove = (e) => {
    if (!panRef.current || panRef.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - panRef.current.x;
    const dy = e.clientY - panRef.current.y;
    if (Math.hypot(dx, dy) > 6 && planClickRef.current) {
      // Treat as pan, not a place click
      planClickRef.current = { ...planClickRef.current, cancelled: true };
    }
    setView(
      clampPan({
        scale: viewRef.current.scale,
        tx: panRef.current.tx + dx,
        ty: panRef.current.ty + dy,
      })
    );
  };

  const onViewportPointerUp = (e) => {
    const origin = planClickRef.current;
    const panning = panRef.current;
    planClickRef.current = null;
    if (panning?.pointerId === e.pointerId) panRef.current = null;
    if (e.button !== 0 && e.button !== -1) return;
    if (e.target?.closest?.('[data-marker]')) return;
    if (e.target?.closest?.('[data-no-place]')) return;
    if (!selectedInstanceId || typeof onPlaceUnit !== 'function') return;
    if (!origin || origin.cancelled) return;
    if (origin.pointerId != null && e.pointerId != null && origin.pointerId !== e.pointerId) {
      return;
    }
    if (Date.now() < suppressPlaceUntilRef.current) return;
    if (Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > 8) return;
    const wrap = imageWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      return;
    }
    // Place only when both press and release are on the drawing
    if (
      origin.x < rect.left ||
      origin.x > rect.right ||
      origin.y < rect.top ||
      origin.y > rect.bottom
    ) {
      return;
    }
    onPlaceUnit(selectedInstanceId, percentFromPointer(rect, e.clientX, e.clientY));
  };

  if (!floorPlanUrl) {
    return (
      <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-8 text-center text-gray-600 flex-1 flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <p className="text-sm font-medium text-gray-800">No floor drawing for this level yet</p>
        <p className="text-xs text-gray-600 max-w-md">
          Upload a PNG/JPG or PDF in <strong>Edit Building → Building structure &amp; drawings</strong> — click
          the part row and upload for each floor.
        </p>
        {typeof onManagePlans === 'function' && (
          <button
            type="button"
            onClick={onManagePlans}
            className="px-4 py-2 rounded-lg bg-violet-700 text-white text-sm hover:bg-violet-800"
          >
            Open Edit Building
          </button>
        )}
      </div>
    );
  }

  if (planImageFailed) {
    return (
      <div className="flex flex-col flex-1 min-h-0 gap-2 min-w-0">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-8 text-center flex-1 flex flex-col items-center justify-center min-h-[40vh] gap-3">
          <p className="text-sm font-medium text-amber-900">Drawing file could not be loaded</p>
          <p className="text-xs text-amber-800 max-w-md">
            The level has a saved file path but the image is missing or unreadable. Upload again in Edit Building.
          </p>
          {typeof onManagePlans === 'function' && (
            <button
              type="button"
              onClick={onManagePlans}
              className="px-4 py-2 rounded-lg bg-violet-700 text-white text-sm hover:bg-violet-800"
            >
              Open Edit Building
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2 min-w-0">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 shrink-0" data-no-place>
          <p className="text-xs font-medium text-gray-600 mb-2">
            Select an SS (number only). If it has subs, pick a sub below, then click the sheet.
          </p>
          <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
            {ssInstances.length === 0 ? (
              <span className="text-sm text-gray-400">No SS on this building. Add SS on the building form first.</span>
            ) : (
              ssInstances.map((inst) => {
                const selected = String(selectedInstanceId) === String(inst.id);
                return (
                  <button
                    key={inst.id}
                    type="button"
                    data-no-place
                    onClick={() => onSelectInstance?.(inst.id)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-transparent border-0 ${
                      selected
                        ? 'text-blue-600 underline'
                        : 'text-slate-700 hover:text-blue-500'
                    }`}
                    title={`${inst.ss_code}${inst.location ? ` — ${inst.location}` : ''}`}
                  >
                    <Plus className={`w-3.5 h-3.5 ${selected ? 'text-blue-600' : 'text-slate-500'}`} />
                    {ssCodeBare(inst.ss_code) || inst.ss_code}
                  </button>
                );
              })
            )}
          </div>
          {selectedInstanceId ? (
            <EquipmentTypeChips
              instance={ssInstances.find((i) => String(i.id) === String(selectedInstanceId))}
              numberingUnits={numberingUnits || units}
              selectedTypeId={selectedEquipmentTypeId}
              onSelectType={onSelectEquipmentType}
              showAllSubs={showAllSubs}
              onShowAllSubsChange={onShowAllSubsChange}
            />
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 shrink-0 flex-wrap">
          <p className="text-[11px] text-gray-500">
            Zoom with +/− · drag to pan when zoomed · wheel / one-finger scrolls the page · click to place
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[11px] text-gray-500 mr-0.5">Label</span>
            <button
              type="button"
              data-zoom-btn
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => bumpLabelPx(-1, e)}
              className="px-2 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-xs font-bold"
              title="Smaller unit labels"
            >
              A−
            </button>
            <span className="text-xs text-gray-600 w-8 text-center tabular-nums">{labelPx}</span>
            <button
              type="button"
              data-zoom-btn
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => bumpLabelPx(1, e)}
              className="px-2 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-xs font-bold"
              title="Larger unit labels"
            >
              A+
            </button>
            <span className="w-px h-5 bg-gray-200 mx-1" aria-hidden />
            <button
              type="button"
              data-zoom-btn
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setScaleAt(view.scale / 1.35)}
              className="p-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-600 w-12 text-center tabular-nums">
              {Math.round(view.scale * 100)}%
            </span>
            <button
              type="button"
              data-zoom-btn
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setScaleAt(view.scale * 1.35)}
              className="p-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-zoom-btn
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setView({ scale: 1, tx: 0, ty: 0 })}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-xs font-medium"
              title="Reset zoom"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Reset
            </button>
          </div>
        </div>

        <div ref={stageHostRef} className="w-full min-w-0 flex justify-center">
          <div
            ref={viewportRef}
            className={`relative overflow-hidden border border-gray-300 rounded-lg bg-slate-200 ${
              selectedInstanceId
                ? view.scale > 1.02
                  ? 'cursor-grab active:cursor-grabbing'
                  : 'cursor-crosshair'
                : view.scale > 1.02
                  ? 'cursor-grab active:cursor-grabbing'
                  : ''
            }`}
            style={{
              width: planBox.w || '100%',
              height: planBox.h || 420,
              maxWidth: '100%',
              // Page scroll over the plan; only lock touch while zoomed (pan)
              touchAction: view.scale > 1.02 ? 'none' : 'pan-y',
            }}
            onPointerDown={onViewportPointerDown}
            onPointerMove={onViewportPointerMove}
            onPointerUp={onViewportPointerUp}
            onPointerCancel={onViewportPointerUp}
          >
            <div
              className="absolute left-0 top-0 origin-top-left will-change-transform"
              style={{
                width: planBox.w || '100%',
                height: planBox.h || '100%',
                transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
                transformOrigin: '0 0',
              }}
            >
              <div
                ref={mapRef}
                className="relative w-full h-full"
                role="presentation"
              >
                <div
                  ref={imageWrapRef}
                  className="relative w-full h-full"
                  style={
                    planBox.w
                      ? { width: planBox.w, height: planBox.h }
                      : undefined
                  }
                >
                  <AuthImage
                    filename={floorPlanUrl}
                    alt="Building floor plan"
                    onError={() => setPlanImageFailed(true)}
                    onLoad={onPlanImageLoad}
                    className="block w-full h-full object-fill select-none pointer-events-none"
                    draggable={false}
                  />
                  {placedDisplay.map((unit) => (
                    <UnitMarker
                      key={unit.id}
                      unit={unit}
                      scale={view.scale}
                      labelPx={labelPx}
                      highlighted={String(highlightUnitId) === String(unit.id)}
                      onPointerDown={handleMarkerPointerDown}
                      onDoubleClick={onUnitDoubleClick || onUnitClick}
                      onContextMenu={onDeleteUnit}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 shrink-0">
          {placed.length} unit{placed.length === 1 ? '' : 's'} with a saved pin
          {autoCount ? ` · ${autoCount} layout-only (dashed — drag to save)` : ''}
          {units.some((u) => String(u.id).startsWith('draft-')) ? ' · * = not saved yet' : ''}
        </p>
    </div>
  );
}
